/**
 * ProducedMana - Renders mana color pips for a card's produced mana.
 * Uses small colored circles (ms-cost text-xs) matching the ColorDots pattern —
 * visually lighter than Mana Cost (which uses full-size + shadow).
 * Returns null when the card doesn't produce mana.
 */
import type { ReactElement } from 'react';

import { costIconFontSize } from '@/components/ManaSymbol/ManaSymbol';
import type { Color } from '@/types/card';

const COLOR_ORDER: Color[] = ['W', 'U', 'B', 'R', 'G'];

const COLOR_NAMES: Record<Color, string> = {
  W: 'White',
  U: 'Blue',
  B: 'Black',
  R: 'Red',
  G: 'Green',
};

export interface ProducedManaProps {
  colors: Color[] | null;
  /** Desired visible height of each icon pill in px (default: inherited) */
  iconSize?: number;
}

export default function ProducedMana({ colors, iconSize }: ProducedManaProps): ReactElement | null {
  if (!colors || colors.length === 0) return null;

  // Sort in WUBRG order for consistency with ColorDots
  const sorted = COLOR_ORDER.filter((c) => colors.includes(c));
  const tooltipText = sorted.map((c) => COLOR_NAMES[c]).join(', ');

  return (
    <span
      className="inline-flex items-center gap-0.5"
      title={`Produces: ${tooltipText}`}
      style={iconSize ? { fontSize: costIconFontSize(iconSize) } : undefined}
    >
      {sorted.map((color) => (
        <i key={color} className={`ms ms-${color.toLowerCase()} ms-cost`} aria-hidden="true" />
      ))}
    </span>
  );
}
